<script setup>
  import { useNotification } from '@/composables/useNotification';

  const { notifications, removeNotification } = useNotification();
</script>

<template>
  <div
    class="pointer-events-none fixed inset-x-0 top-0 z-[1060] flex items-start justify-center p-6"
  >
    <TransitionGroup name="notification" tag="div" class="w-full max-w-md space-y-3">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        class="pointer-events-auto w-full transform overflow-hidden rounded-lg shadow-lg transition-all"
        :class="{
          'border-l-4 border-green-400 bg-green-50 text-green-800': notification.type === 'success',
          'border-l-4 border-blue-400 bg-blue-50 text-blue-800': notification.type === 'info',
          'border-l-4 border-yellow-400 bg-yellow-50 text-yellow-800':
            notification.type === 'warning',
          'border-l-4 border-red-400 bg-red-50 text-red-800': notification.type === 'error',
        }"
      >
        <div class="p-4">
          <div class="flex items-start">
            <div v-if="notification.type === 'success'" class="shrink-0">
              <svg
                class="h-5 w-5 text-green-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div v-else-if="notification.type === 'info'" class="shrink-0">
              <svg
                class="h-5 w-5 text-blue-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div v-else-if="notification.type === 'warning'" class="shrink-0">
              <svg
                class="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div v-else-if="notification.type === 'error'" class="shrink-0">
              <svg
                class="h-5 w-5 text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>

            <div class="ml-3 flex-1">
              <p class="text-sm font-medium">{{ notification.message }}</p>
            </div>

            <div class="ml-4 flex shrink-0">
              <button
                class="inline-flex text-gray-400 transition duration-150 ease-in-out focus:text-gray-500 focus:outline-hidden"
                @click="removeNotification(notification.id)"
              >
                <svg
                  class="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>
